<script setup lang="ts">
import type { VideoProgressBarProps } from '.';

import { useEventListener } from '@vueuse/core';

defineProps<VideoProgressBarProps>();

const emit = defineEmits<{
	seek: [ratio: number];
}>();

const progressBarRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

function emitSeekFromPosition(clientX: number) {
	if (!progressBarRef.value) return;

	const rect = progressBarRef.value.getBoundingClientRect();
	const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
	emit('seek', ratio);
}

function handleSeek(event: MouseEvent) {
	event.stopPropagation();
	emitSeekFromPosition(event.clientX);
}

function handleDragStart(event: MouseEvent | TouchEvent) {
	event.stopPropagation();
	event.preventDefault();

	const clientX =
		'touches' in event ? event.touches[0]?.clientX : event.clientX;

	if (clientX === undefined) return;

	isDragging.value = true;
	emitSeekFromPosition(clientX);
}

useEventListener(
	() => (isDragging.value ? document : null),
	'mousemove',
	(event: MouseEvent) => {
		event.preventDefault();
		emitSeekFromPosition(event.clientX);
	}
);

useEventListener(
	() => (isDragging.value ? document : null),
	'touchmove',
	(event: TouchEvent) => {
		event.preventDefault();
		const touch = event.touches[0];
		if (touch) {
			emitSeekFromPosition(touch.clientX);
		}
	},
	{ passive: false }
);

useEventListener(
	() => (isDragging.value ? document : null),
	'mouseup',
	() => {
		isDragging.value = false;
	}
);

useEventListener(
	() => (isDragging.value ? document : null),
	'touchend',
	() => {
		isDragging.value = false;
	}
);
</script>

<template>
	<div
		ref="progressBarRef"
		class="relative h-1 w-full cursor-pointer overflow-hidden rounded-full bg-white/30 py-0.5 backdrop-blur-sm"
		@click.stop="handleSeek"
		@mousedown.stop="handleDragStart"
		@touchstart.stop="handleDragStart"
	>
		<div
			class="absolute left-0 top-0 h-1 rounded-full bg-white"
			:class="isDragging ? '' : 'transition-[width] duration-200'"
			:style="{ width: `${progress}%` }"
		/>
	</div>
</template>
